/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useParams, useNavigate } from 'react-router-dom';
import { type FC, type PropsWithChildren } from 'react';

import { GlobalLayout } from '@coze-foundation/layout';
import { useCreateBotAction } from '@coze-foundation/global';
import { RequireAuthContainer } from '@coze-foundation/account-ui-adapter';
import { I18n } from '@coze-arch/i18n';
import { useRouteConfig } from '@coze-arch/bot-hooks';
import { useSpaceStore } from '@coze-foundation/space-store';
import {
  IconCozPlusCircle,
  IconCozWorkspace,
  IconCozWorkspaceFill,
  IconCozCompass,
  IconCozCompassFill,
  IconCozDocument,
  IconCozUserPrompt,
} from '@coze-arch/coze-design/icons';
import { redirectToRokidAccountCenter } from '@rokid/account';
import { AccountDropdown } from '../account-dropdown';
import { useHasSider } from './hooks/use-has-sider';
import { useUserInfo } from '@coze-arch/foundation-sdk';

export const GlobalLayoutComposed: FC<PropsWithChildren> = ({ children }) => {
  const config = useRouteConfig();
  const hasSider = useHasSider();
  const { space_id } = useParams();
  const spaceId = useSpaceStore(state => state.space.id);
  const nav = useNavigate();
  const userInfo = useUserInfo();
  const { createBot, createBotModal, cancelCreateBot } = useCreateBotAction({
    currentSpaceId: space_id,
  });
  const isLogin = userInfo ? true : false;
  const extrasCfgs = [
    //  rokid 替换文档、添加登录
    {
      icon: <IconCozUserPrompt />,
      tooltip: false,
      btnText: I18n.t('login_button_text'),
      onClick: () => {
        redirectToRokidAccountCenter(window.location.href);
      },
      dataTestId: 'layout_login-button',
    },
    {
      icon: <IconCozDocument />,
      tooltip: false,
      btnText: I18n.t('menu_documents'),
      onClick: () => {
        // cp-disable-next-line
        window.open('https://rokid.yuque.com/ub8h5n/hth52o/bt1vv4qwaqx5se96');
      },
      dataTestId: 'layout_document-button',
    },
  ];
  if (isLogin) {
    extrasCfgs.shift();
  }
  return (
    <RequireAuthContainer
      needLogin={!!config.requireAuth}
      loginOptional={!!config.requireAuthOptional}
    >
      <GlobalLayout
        hasSider={hasSider}
        banner={null}
        actions={[
          {
            tooltip: false,
            btnText: I18n.t('creat_tooltip_create'),
            icon: <IconCozPlusCircle />,
            onClick: spaceId
              ? createBot
              : () => {
                  redirectToRokidAccountCenter(window.location.href);
                },
            dataTestId: 'layout_create-agent-button',
          },
        ]}
        menus={[
          {
            title: I18n.t('navigation_workspace'),
            icon: <IconCozWorkspace />,
            activeIcon: <IconCozWorkspaceFill />,
            path: '/space',
            dataTestId: 'layout_workspace-button',
          },
          {
            title: I18n.t('menu_title_store'),
            icon: <IconCozCompass />,
            activeIcon: <IconCozCompassFill />,
            path: '/explore',
            dataTestId: 'layout_explore-button',
          },
        ]}
        extras={extrasCfgs}
        footer={<AccountDropdown />}
        onClickLogo={() => {
          nav('/space/home');
        }}
      >
        {children}
        {createBotModal}
      </GlobalLayout>
    </RequireAuthContainer>
  );
};
